import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, MenuItem,
    Stack, Chip, Avatar, IconButton, Tooltip, LinearProgress,
    Fade
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Description as DocIcon,
    Visibility as ViewIcon,
    Error as ExpiredIcon,
    Warning as WarningIcon,
    CheckCircle as ValidIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { formatDDMMYYYY } from "../../services/datetimeService";

const DocumentExpiryReport = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [daysFilter, setDaysFilter] = useState('30');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const h = { 'X-Tenant': tenant };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/web-reports/document-expiry?days=${daysFilter}`, { withCredentials: true, headers: h });
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (err) {
            console.error("Report fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant, daysFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusChip = (days) => {
        if (days < 0) return <Chip icon={<ExpiredIcon />} label="Expired" color="error" size="small" sx={{ fontWeight: 800 }} />;
        if (days <= 30) return <Chip icon={<WarningIcon />} label={`${days} Days Left`} color="warning" size="small" sx={{ fontWeight: 800 }} />;
        return <Chip icon={<ValidIcon />} label="Valid" color="success" size="small" sx={{ fontWeight: 800, bgcolor: '#ecfdf5', color: '#10b981' }} />;
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
                            <IconButton onClick={() => navigate('/reports')} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}><ArrowBackIcon /></IconButton>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>Document Expiry Audit</Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Monitoring validity periods for compliance and renewal planning.</Typography>
                            </Box>
                        </Stack>

                        <Paper sx={{ p: 3, borderRadius: 4, mb: 3, border: '1px solid #e2e8f0', display: 'flex', gap: 3, alignItems: 'center' }}>
                            <Box sx={{ minWidth: 200 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', display: 'block', mb: 1, textTransform: 'uppercase' }}>Expiry Horizon</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={daysFilter}
                                    onChange={(e) => setDaysFilter(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                                >
                                    <MenuItem value="30">Next 30 Days</MenuItem>
                                    <MenuItem value="60">Next 60 Days</MenuItem>
                                    <MenuItem value="90">Next 90 Days</MenuItem>
                                    <MenuItem value="365">Next Year</MenuItem>
                                    <MenuItem value="">Show All (With Expiry)</MenuItem>
                                </TextField>
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main' }}>{data.length}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8' }}>Alerts Found</Typography>
                            </Box>
                        </Paper>

                        <TableContainer component={Paper} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                            {loading && <LinearProgress sx={{ height: 4 }} />}
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569' }}>STAFF</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569' }}>DOCUMENT TYPE</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569' }}>EXPIRY DATE</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569' }}>STATUS</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textAlign: 'right' }}>ACTIONS</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((row) => (
                                        <TableRow key={row.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar sx={{ bgcolor: '#f1f5f9', color: 'primary.main', fontWeight: 800, fontSize: 12 }}>{row.full_name?.charAt(0)}</Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{row.full_name}</Typography>
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>{row.staff_code} • {row.department}</Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <DocIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.document_type}</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: row.days_remaining < 0 ? '#ef4444' : '#334155' }}>
                                                    {formatDDMMYYYY(row.expiry_date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{getStatusChip(row.days_remaining)}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title="View Profile"><IconButton size="small" onClick={() => navigate(`/staff/${row.staff_id}`)} sx={{ color: 'primary.main' }}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                                                    <Tooltip title="View Document"><IconButton size="small" onClick={() => window.open(`${process.env.REACT_APP_API_URL1}/admin/documents/${row.dms_id}/preview?tenant=${tenant}`, '_blank')}><DocIcon fontSize="small" /></IconButton></Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {data.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ py: 10, textAlign: 'center' }}>
                                                <Typography variant="body1" sx={{ color: '#94a3b8', mb: 1 }}>No expiring documents found.</Typography>
                                                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>Adjust filters or horizon to see more records.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Fade>
            </Box>
        </Box>
    );
};

export default DocumentExpiryReport;
